import type { DateOnlyString } from '@/lib/utils/date-only';

import type { AssumptionKey, Assumptions, FoodWastePeriod } from './types';

/** Calendar used for presets and for "today": the canteen's local day. */
export const FOOD_WASTE_TIME_ZONE = 'Europe/Lisbon';

/** Counted ranges up to 16 weeks are shown per week, longer ones per month. */
export const WEEKLY_VIEW_MAX_DAYS = 112;

/**
 * Lower bound of "All time" before its real start is known: the period then
 * starts at the first menu week of the selected canteens.
 */
export const ALL_TIME_QUERY_START: DateOnlyString = '2000-01-01';

/**
 * Pickups were scanned from this day on. Orders on earlier days count as
 * collected (not collected = 0): no scan does not mean no pickup.
 */
export const PICKUP_SCAN_START: DateOnlyString = '2026-03-01';

export const FOOD_WASTE_PERIODS = [
  'this-month',
  'last-month',
  'ytd',
  'all',
  'custom',
] as const satisfies readonly FoodWastePeriod[];

export const DEFAULT_PERIOD: FoodWastePeriod = 'this-month';

/** Message keys under `foodWaste.filters.period`. */
export const PERIOD_LABEL_KEYS: Record<FoodWastePeriod, string> = {
  'this-month': 'thisMonth',
  'last-month': 'lastMonth',
  ytd: 'ytd',
  all: 'allTime',
  custom: 'custom',
};

export const ALL_CANTEENS = 'all';

export const ALL_COMPANIES = 'all';

export interface AssumptionDefinition {
  key: AssumptionKey;
  defaultValue: number;
  min: number;
  max: number;
  step: number;
  /** Decimal places shown in the input. */
  decimals: number;
}

/**
 * Defaults with their editable ranges. Units: rate in percent, weight in
 * grams, cost in euro per meal, CO2e in kg per kg of food.
 */
export const ASSUMPTIONS: Record<AssumptionKey, AssumptionDefinition> = {
  rate: { key: 'rate', defaultValue: 30, min: 5, max: 40, step: 1, decimals: 0 },
  weight: { key: 'weight', defaultValue: 500, min: 250, max: 700, step: 10, decimals: 0 },
  cost: { key: 'cost', defaultValue: 5, min: 1.5, max: 8, step: 0.1, decimals: 2 },
  co2: { key: 'co2', defaultValue: 3, min: 1.5, max: 4.5, step: 0.1, decimals: 1 },
};

export const ASSUMPTION_KEYS: readonly AssumptionKey[] = ['rate', 'weight', 'cost', 'co2'];

export const DEFAULT_ASSUMPTIONS: Assumptions = {
  rate: ASSUMPTIONS.rate.defaultValue,
  weight: ASSUMPTIONS.weight.defaultValue,
  cost: ASSUMPTIONS.cost.defaultValue,
  co2: ASSUMPTIONS.co2.defaultValue,
};
